import React from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    FaArrowRight,
    FaBell,
    FaCalendarAlt,
    FaCogs,
    FaHome,
    FaTasks,
    FaTools,
    FaUserCog,
    FaWarehouse,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';

const surfaceBorder = 'rgba(15, 23, 42, 0.08)';
const mutedText = '#64748b';
const titleText = '#0f172a';
const accent = '#1d4ed8';

const routeByGroup = {
    Admin: '/ferramentasadmin',
    Escritorio: '/escritorio',
    Corte: '/corte',
    Armazem: '/armazem',
    Digital: '/digital',
};

const QuickActionCard = ({ title, description, icon, onClick }) => (
    <Paper
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
            }
        }}
        sx={{
            p: 2,
            height: '100%',
            borderRadius: 2.5,
            border: `1px solid ${surfaceBorder}`,
            cursor: 'pointer',
            backgroundColor: '#fff',
            transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)',
                borderColor: 'rgba(29, 78, 216, 0.22)',
            },
            '&:focus-visible': {
                outline: `2px solid ${accent}`,
                outlineOffset: 2,
            },
        }}
    >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    color: accent,
                    backgroundColor: 'rgba(29, 78, 216, 0.08)',
                    border: '1px solid rgba(29, 78, 216, 0.12)',
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: titleText, fontSize: '0.95rem' }}>
                    {title}
                </Typography>
                <Typography sx={{ color: mutedText, fontSize: '0.82rem', mt: 0.25 }}>
                    {description}
                </Typography>
            </Box>
        </Stack>
    </Paper>
);

const Dashboard = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user'));

    if (!user) {
        return (
            <DashboardLayout>
                <Typography variant="h6" color="error">
                    Erro: Usuário não encontrado.
                </Typography>
            </DashboardLayout>
        );
    }

    const firstName = (user.nome_completo || '').split(' ')[0] || 'Utilizador';
    const isAdmin = user.seccao === 'Admin';
    const isEscritorio = user.seccao === 'Escritorio';
    const primaryAreaRoute = routeByGroup[user.seccao];

    const quickActions = [
        {
            key: 'ferias',
            title: 'Marcar Férias',
            description: 'Criar ou rever pedidos de férias.',
            icon: <FaCalendarAlt />,
            onClick: () => navigate('/marcar-ferias'),
        },
        {
            key: 'faltas',
            title: 'Faltas',
            description: 'Consultar e gerir faltas e justificações.',
            icon: <FaTasks />,
            onClick: () => navigate('/faltas'),
        },
        {
            key: 'account',
            title: 'Definições',
            description: 'Atualizar dados e preferências da conta.',
            icon: <FaUserCog />,
            onClick: () => navigate('/account-settings', { state: { user } }),
        },
        {
            key: 'stocks',
            title: 'Stocks',
            description: 'Aceder ao controlo de inventário.',
            icon: <FaWarehouse />,
            onClick: () => navigate('/stocks'),
        },
        {
            key: 'cliches',
            title: 'Clichês & Moldes',
            description: 'Gestão de ferramentas e registos.',
            icon: <FaTools />,
            onClick: () => navigate('/cliches-moldes'),
        },
        {
            key: 'operacional',
            title: 'Operacional',
            description: 'Abrir ferramentas operacionais.',
            icon: <FaCogs />,
            onClick: () => navigate('/op'),
        },
    ];

    if (isAdmin || isEscritorio) {
        quickActions.unshift({
            key: 'escritorio',
            title: 'Escritório',
            description: 'Aprovações de férias e gestão administrativa.',
            icon: <FaHome />,
            onClick: () => navigate('/escritorio'),
        });
    }

    if (isAdmin) {
        quickActions.unshift({
            key: 'admin-tools',
            title: 'Ferramentas Admin',
            description: 'Acesso às ferramentas administrativas.',
            icon: <FaCogs />,
            onClick: () => navigate('/ferramentasadmin'),
        });
    }

    const shortcuts = [
        { label: 'Home', path: '/home' },
        { label: 'Stocks', path: '/stocks' },
        { label: 'Clichês', path: '/cliches-moldes' },
        { label: 'Operacional', path: '/op' },
    ];

    if (primaryAreaRoute && !shortcuts.some((item) => item.path === primaryAreaRoute)) {
        shortcuts.unshift({ label: `Área ${user.seccao}`, path: primaryAreaRoute });
    }

    return (
        <DashboardLayout>
            <Box sx={{ display: 'grid', gap: 2.5 }}>
                <Paper
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 3,
                        border: `1px solid ${surfaceBorder}`,
                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                        boxShadow: '0 10px 28px rgba(15,23,42,0.04)',
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: titleText,
                                    fontWeight: 800,
                                    letterSpacing: '-0.02em',
                                    fontSize: { xs: '1.5rem', sm: '2rem' },
                                }}
                            >
                                Bem-vindo, {firstName}
                            </Typography>
                            <Typography sx={{ color: mutedText, mt: 0.75, fontSize: '0.95rem' }}>
                                Aceda rapidamente às áreas principais e acompanhe os seus pedidos de forma simples.
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                <Chip
                                    label={`Secção: ${user.seccao || 'N/A'}`}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(15,23,42,0.04)',
                                        border: `1px solid ${surfaceBorder}`,
                                        color: titleText,
                                        fontWeight: 600,
                                    }}
                                />
                                {user.numero_empregado && (
                                    <Chip
                                        label={`Nº ${user.numero_empregado}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'rgba(15,23,42,0.04)',
                                            border: `1px solid ${surfaceBorder}`,
                                            color: titleText,
                                            fontWeight: 600,
                                        }}
                                    />
                                )}
                            </Stack>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/marcar-ferias')}
                                endIcon={<FaArrowRight />}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: accent,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1e40af',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                Marcar Férias
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(isAdmin || isEscritorio ? '/escritorio' : '/account-settings', isAdmin || isEscritorio ? undefined : { state: { user } })}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderColor: surfaceBorder,
                                    color: titleText,
                                    '&:hover': {
                                        borderColor: 'rgba(15,23,42,0.18)',
                                        backgroundColor: 'rgba(15,23,42,0.02)',
                                    },
                                }}
                            >
                                {isAdmin || isEscritorio ? 'Abrir Escritório' : 'Definições de Conta'}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                <Box>
                    <Typography sx={{ color: titleText, fontWeight: 700, mb: 1.25, fontSize: '1rem' }}>
                        Ações rápidas
                    </Typography>
                    <Grid container spacing={2}>
                        {quickActions.map((action) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={action.key}>
                                <QuickActionCard {...action} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Paper
                            sx={{
                                p: 2.25,
                                borderRadius: 2.5,
                                border: `1px solid ${surfaceBorder}`,
                                height: '100%',
                                backgroundColor: '#fff',
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 1.5,
                                        display: 'grid',
                                        placeItems: 'center',
                                        color: accent,
                                        backgroundColor: 'rgba(29, 78, 216, 0.08)',
                                    }}
                                >
                                    <FaHome />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: titleText }}>
                                    Atalhos frequentes
                                </Typography>
                            </Stack>
                            <Typography sx={{ color: mutedText, fontSize: '0.9rem', mb: 1.5 }}>
                                Navegação rápida para as áreas mais usadas no dia a dia.
                            </Typography>

                            <Stack spacing={1}>
                                {shortcuts.map((shortcut) => (
                                    <Button
                                        key={shortcut.path}
                                        onClick={() => navigate(shortcut.path)}
                                        variant="text"
                                        sx={{
                                            justifyContent: 'space-between',
                                            textTransform: 'none',
                                            color: titleText,
                                            borderRadius: 1.75,
                                            px: 1.25,
                                            py: 1,
                                            border: `1px solid ${surfaceBorder}`,
                                            backgroundColor: '#fff',
                                            '&:hover': {
                                                backgroundColor: 'rgba(15,23,42,0.02)',
                                            },
                                        }}
                                        endIcon={<FaArrowRight size={12} />}
                                    >
                                        {shortcut.label}
                                    </Button>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Paper
                            sx={{
                                p: 2.25,
                                borderRadius: 2.5,
                                border: `1px solid ${surfaceBorder}`,
                                height: '100%',
                                backgroundColor: '#fff',
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 1.5,
                                        display: 'grid',
                                        placeItems: 'center',
                                        color: accent,
                                        backgroundColor: 'rgba(29, 78, 216, 0.08)',
                                    }}
                                >
                                    <FaBell />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: titleText }}>
                                    Resumo rápido
                                </Typography>
                            </Stack>

                            <Typography sx={{ color: mutedText, fontSize: '0.9rem', mb: 1.5 }}>
                                As notificações ficam disponíveis no topo. Use os atalhos acima para aprovar pedidos,
                                marcar férias e atualizar os seus dados.
                            </Typography>

                            <Divider sx={{ mb: 1.5 }} />

                            <Stack spacing={1}>
                                <Box>
                                    <Typography sx={{ color: titleText, fontWeight: 600, fontSize: '0.88rem' }}>
                                        Férias
                                    </Typography>
                                    <Typography sx={{ color: mutedText, fontSize: '0.84rem' }}>
                                        Marque dias e acompanhe estados (aprovado, pendente e gozados).
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ color: titleText, fontWeight: 600, fontSize: '0.88rem' }}>
                                        Faltas
                                    </Typography>
                                    <Typography sx={{ color: mutedText, fontSize: '0.84rem' }}>
                                        Registe faltas e anexos de justificação quando necessário.
                                    </Typography>
                                </Box>
                                {(isAdmin || isEscritorio) && (
                                    <Box>
                                        <Typography sx={{ color: titleText, fontWeight: 600, fontSize: '0.88rem' }}>
                                            Aprovações
                                        </Typography>
                                        <Typography sx={{ color: mutedText, fontSize: '0.84rem' }}>
                                            A secção Escritório centraliza aprovação/rejeição de pedidos.
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </DashboardLayout>
    );
};

export default Dashboard;
